import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { eventBatchAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import AdminDatePicker from '../components/AdminDatePicker.jsx';
import { PageHeader } from '../components/ui';
import './ModuleAdmin.css';
import './EventBatchForm.css';

const emptyForm = {
  trekId: '',
  code: '',
  startDate: '',
  endDate: '',
  whatsappLink: '',
  prepVideoUrl: '',
  showTicketsToParticipants: false,
  isHighlighted: false,
  status: 'upcoming',
  isActive: true,
  customizeAvailability: false
};

function buildAvailabilityDraft(trek, saved) {
  const rawPkgs = (trek?.packages || []).filter((p) => !p.hidden && p.hide !== true);
  const sortedPkgs = [...rawPkgs].sort((a, b) => {
    if (!!b.isMain !== !!a.isMain) return (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0);
    return (Number(a.priority) || 0) - (Number(b.priority) || 0);
  });
  const packages = {};
  for (const p of sortedPkgs) {
    const id = String(p.id);
    const s = saved?.packages?.[id];
    packages[id] = {
      enabled: s?.enabled !== false,
      vacancy:
        s?.vacancy !== undefined && s?.vacancy !== null && s?.vacancy !== ''
          ? s.vacancy
          : p.defaultVacancy != null && p.defaultVacancy !== ''
            ? p.defaultVacancy
            : ''
    };
  }
  const stayOptions = {};
  for (const o of trek?.stayOptions || []) {
    const id = String(o.id || '').trim();
    if (!id) continue;
    const s = saved?.stayOptions?.[id];
    stayOptions[id] = {
      enabled: s?.enabled !== false,
      vacancy:
        s?.vacancy !== undefined && s?.vacancy !== null && s?.vacancy !== '' ? s.vacancy : ''
    };
  }
  return { packages, stayOptions };
}

function normalizeAvailabilityForApi(draft) {
  const packages = {};
  for (const [id, row] of Object.entries(draft.packages || {})) {
    const v = row.vacancy;
    packages[id] = {
      enabled: row.enabled !== false,
      vacancy: v === '' || v == null ? null : Number(v)
    };
  }
  const stayOptions = {};
  for (const [id, row] of Object.entries(draft.stayOptions || {})) {
    const v = row.vacancy;
    stayOptions[id] = {
      enabled: row.enabled !== false,
      vacancy: v === '' || v == null ? null : Number(v)
    };
  }
  return { packages, stayOptions };
}

function batchPrefixFromTrekFallback(trek) {
  const slug = String(trek?.slug || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '')
    .replace(/^-+|-+$/g, '');
  const parts = slug.split('-').filter(Boolean);
  let s = parts.map((w) => w[0]).join('').toUpperCase();
  if (s.length < 4) {
    const name = String(trek?.name || '').replace(/[^a-zA-Z\s]/g, '');
    const words = name.split(/\s+/).filter(Boolean);
    for (const w of words) {
      if (s.length >= 4) break;
      const c = w[0]?.toUpperCase();
      if (c && !s.includes(c)) s += c;
    }
  }
  return s.slice(0, 4).padEnd(4, 'X');
}

function batchPrefixFromTrek(trek) {
  if (!trek) return 'XXXX';
  const c = String(trek.code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (c.length >= 2) return c.slice(0, 4);
  return batchPrefixFromTrekFallback(trek);
}

function suggestLegacyBatchCode(trek, startDate) {
  if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return '';
  const prefix = batchPrefixFromTrek(trek);
  const ymd = startDate.replace(/-/g, '');
  return `${prefix}${ymd}00`;
}

const EventBatchForm = () => {
  const { batchId } = useParams();
  const isEdit = Boolean(batchId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { permissions } = useAuth();
  const canManage =
    permissions.includes('*') || permissions.includes('event_batches_manage');

  const batchCodeUserLocked = useRef(false);
  const [form, setForm] = useState(emptyForm);
  const [availabilityDraft, setAvailabilityDraft] = useState({ packages: {}, stayOptions: {} });
  const [savedPackageAvailability, setSavedPackageAvailability] = useState(null);
  const [trekSearchOpen, setTrekSearchOpen] = useState(false);
  const [trekSearchInput, setTrekSearchInput] = useState('');

  const { data: existingRes, isLoading: loadingBatch } = useQuery(
    ['event-batch', batchId],
    () => eventBatchAPI.get(batchId),
    { enabled: isEdit && !!batchId }
  );
  const existing = existingRes?.data?.data;

  const { data: trekRes } = useQuery(['treks', 'for-batch-form'], () => trekAPI.list({ isActive: true, limit: 500 }));
  const treks = trekRes?.data?.data || [];

  const { data: trekDetailRes } = useQuery(
    ['trek-batch-form', form.trekId],
    () => trekAPI.get(form.trekId),
    { enabled: Boolean(form.trekId) }
  );
  const trekDetail = trekDetailRes?.data?.data;

  useEffect(() => {
    if (!form.trekId || !trekDetail?.id || !form.customizeAvailability) return;
    setAvailabilityDraft(buildAvailabilityDraft(trekDetail, savedPackageAvailability));
  }, [form.trekId, trekDetail?.id, form.customizeAvailability, savedPackageAvailability]);

  useEffect(() => {
    if (!isEdit) {
      setForm(emptyForm);
      setSavedPackageAvailability(null);
      setAvailabilityDraft({ packages: {}, stayOptions: {} });
      batchCodeUserLocked.current = false;
      return;
    }
    if (!existing) return;
    setSavedPackageAvailability(existing.packageAvailability || null);
    batchCodeUserLocked.current = true;
    setForm({
      trekId: existing.trekId || '',
      code: existing.code || '',
      startDate: existing.startDate || '',
      endDate: existing.endDate || '',
      whatsappLink: existing.whatsappLink || '',
      prepVideoUrl: existing.prepVideoUrl || '',
      showTicketsToParticipants: !!existing.showTicketsToParticipants,
      isHighlighted: !!existing.isHighlighted,
      status: existing.status || 'upcoming',
      isActive: !!existing.isActive,
      customizeAvailability: existing.packageAvailability != null
    });
  }, [isEdit, existing]);

  const treksFilteredForSelect = useMemo(() => {
    const q = (trekSearchInput || '').toLowerCase().trim();
    if (!q) return treks;
    return treks.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [treks, trekSearchInput]);

  const createMutation = useMutation((payload) => eventBatchAPI.create(payload), {
    onSuccess: () => {
      batchCodeUserLocked.current = false;
      queryClient.invalidateQueries(['event-batches']);
      showToast({ message: 'Event batch created.', severity: 'success' });
      navigate('/event-batches');
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message;
      showToast({ message: msg || 'Failed to create batch.', severity: 'error' });
    }
  });

  const updateMutation = useMutation(({ id, payload }) => eventBatchAPI.update(id, payload), {
    onSuccess: () => {
      batchCodeUserLocked.current = false;
      queryClient.invalidateQueries(['event-batches']);
      queryClient.invalidateQueries(['event-batch', batchId]);
      showToast({ message: 'Event batch updated.', severity: 'success' });
      navigate('/event-batches');
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message;
      showToast({ message: msg || 'Failed to update batch.', severity: 'error' });
    }
  });

  const applySuggestedBatchCode = (trek, nextStart, currentCode) => {
    if (isEdit) return currentCode;
    if (batchCodeUserLocked.current) return currentCode;
    if (!trek || !nextStart) return currentCode;
    const suggested = suggestLegacyBatchCode(trek, nextStart);
    return suggested || currentCode;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canManage) return;
    const { customizeAvailability, ...rest } = form;
    let payload = { ...rest };
    if (!String(payload.code || '').trim() && payload.startDate && payload.trekId) {
      const tr = treks.find((t) => t.id === payload.trekId);
      if (tr) {
        payload = { ...payload, code: suggestLegacyBatchCode(tr, payload.startDate) };
      }
    }
    payload.packageAvailability = customizeAvailability ? normalizeAvailabilityForApi(availabilityDraft) : null;
    if (isEdit) updateMutation.mutate({ id: batchId, payload });
    else createMutation.mutate(payload);
  };

  if (isEdit && loadingBatch && !existing) {
    return (
      <div className="module-page ebb-page">
        <div className="ebb-loading">Loading batch…</div>
      </div>
    );
  }

  if (isEdit && !loadingBatch && !existing) {
    return (
      <div className="module-page ebb-page">
        <AdminBackLink to="/event-batches" label="Back to Events" />
        <p className="ebb-loading">Event batch not found.</p>
      </div>
    );
  }

  const selectedTrek = treks.find((t) => t.id === form.trekId);
  const title = isEdit ? `Edit event batch` : 'New event batch';
  const titleExtra = isEdit && existing?.code ? ` · ${existing.code}` : '';

  return (
    <div className="module-page ebb-page">
      <AdminBackLink to="/event-batches" label="Back to Events" />
      <PageHeader
        title={`${title}${titleExtra}`}
        subtitle="Set trek, dates, and optional per-departure travel/stay rules. Saved batches appear on the website departure picker and booking flow."
      />

      <form onSubmit={onSubmit}>
        <section className="ebb-section">
          <h2 className="ebb-section-title">Basics</h2>
          <p className="ebb-section-desc">Choose the trek and the batch code and date range for this departure.</p>
          <div className="ebb-form-grid module-form-grid">
            <div className="ebb-trek-wrap events-form-trek-wrap">
              <label>Trek *</label>
              <input
                type="text"
                placeholder="Search and select trek…"
                value={form.trekId ? (selectedTrek?.name || '') : trekSearchInput}
                onFocus={() => setTrekSearchOpen(true)}
                onBlur={() => setTimeout(() => setTrekSearchOpen(false), 180)}
                onChange={(e) => {
                  setTrekSearchInput(e.target.value);
                  setForm((p) => ({ ...p, trekId: '' }));
                }}
                className="events-trek-search-input"
                required={!form.trekId}
                disabled={!canManage}
              />
              {trekSearchOpen && (
                <div className="events-trek-dropdown">
                  {treksFilteredForSelect.length === 0 ? (
                    <div className="events-trek-dropdown-empty">No treks found</div>
                  ) : (
                    treksFilteredForSelect.slice(0, 20).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`events-trek-dropdown-item ${form.trekId === t.id ? 'selected' : ''}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setForm((p) => {
                            const next = { ...p, trekId: t.id };
                            if (!isEdit && !batchCodeUserLocked.current && p.startDate) {
                              next.code = suggestLegacyBatchCode(t, p.startDate);
                            }
                            return next;
                          });
                          setTrekSearchInput('');
                          setTrekSearchOpen(false);
                        }}
                      >
                        {t.name}
                      </button>
                    ))
                  )}
                </div>
              )}
              {form.trekId && !trekSearchOpen && selectedTrek && (
                <span className="events-trek-selected-hint">Selected: {selectedTrek.name}</span>
              )}
            </div>

            <div>
              <label>Batch code *</label>
              <input
                value={form.code}
                onChange={(e) => {
                  const v = e.target.value.trim().toUpperCase();
                  batchCodeUserLocked.current = Boolean(v);
                  setForm((p) => ({ ...p, code: v }));
                }}
                placeholder="e.g. INKR2026062800"
                required
                disabled={!canManage}
              />
              <span className="events-code-hint ebb-hint">
                {isEdit
                  ? 'Must be unique across all batches.'
                  : 'Auto-filled from trek code + start date. Edit if you need a different suffix than 00.'}
              </span>
            </div>
            <div>
              <AdminDatePicker
                label="Start date *"
                value={form.startDate}
                maxDate={form.endDate}
                required
                disabled={!canManage}
                onChange={(startDate) => {
                  setForm((p) => ({
                    ...p,
                    startDate,
                    code: applySuggestedBatchCode(treks.find((tr) => tr.id === p.trekId), startDate, p.code)
                  }));
                }}
              />
            </div>
            <div>
              <AdminDatePicker
                label="End date *"
                value={form.endDate}
                minDate={form.startDate}
                required
                disabled={!canManage}
                onChange={(endDate) => setForm((p) => ({ ...p, endDate }))}
              />
            </div>
          </div>
        </section>

        <section className="ebb-section">
          <h2 className="ebb-section-title">Options</h2>
          <p className="ebb-section-desc">Links, visibility, and status for this batch.</p>
          <div className="ebb-form-grid module-form-grid">
            <div className="ebb-form-full">
              <label>WhatsApp group link</label>
              <input
                value={form.whatsappLink}
                onChange={(e) => setForm((p) => ({ ...p, whatsappLink: e.target.value }))}
                placeholder="https://chat.whatsapp.com/…"
                disabled={!canManage}
              />
            </div>
            <div className="ebb-form-full">
              <label>Prep video (YouTube URL)</label>
              <input
                value={form.prepVideoUrl}
                onChange={(e) => setForm((p) => ({ ...p, prepVideoUrl: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=…"
                disabled={!canManage}
              />
            </div>
            <div>
              <label>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                disabled={!canManage}
              >
                <option value="upcoming">upcoming</option>
                <option value="completed">completed</option>
                <option value="cancelled">cancelled</option>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
            <div>
              <label>Highlighted</label>
              <select
                value={String(form.isHighlighted)}
                onChange={(e) => setForm((p) => ({ ...p, isHighlighted: e.target.value === 'true' }))}
                disabled={!canManage}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <label>Show tickets to participants</label>
              <select
                value={String(form.showTicketsToParticipants)}
                onChange={(e) => setForm((p) => ({ ...p, showTicketsToParticipants: e.target.value === 'true' }))}
                disabled={!canManage}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <label>Active</label>
              <select
                value={String(form.isActive)}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}
                disabled={!canManage}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
        </section>

        <section className="ebb-section">
          <h2 className="ebb-section-title">Travel &amp; stay for this date</h2>
          <p className="ebb-section-desc">
            By default, all trek packages and stay options use trek-level settings. Turn on customization to match the
            legacy “Add Event” form: enable/disable each travel row and set vacancies for this departure only.
          </p>
          <label className="ebb-checkbox-row ebb-form-full">
            <input
              type="checkbox"
              checked={form.customizeAvailability}
              onChange={(e) => {
                const on = e.target.checked;
                setForm((p) => ({ ...p, customizeAvailability: on }));
                if (on && trekDetail?.id) {
                  setAvailabilityDraft(buildAvailabilityDraft(trekDetail, savedPackageAvailability));
                }
              }}
              disabled={!canManage}
            />
            <span>
              Customize package &amp; stay availability for this departure
              <span className="ebb-hint" style={{ display: 'block', marginTop: '0.35rem', fontWeight: 400 }}>
                When off, the site uses all visible trek packages and stay options. When on, use the tables below.
              </span>
            </span>
          </label>

          {form.customizeAvailability && form.trekId && trekDetail?.id ? (
            <div className="ebb-availability-panel">
              <h3 className="ebb-section-title" style={{ fontSize: '0.95rem' }}>
                Packages (join-from / travel)
              </h3>
              <div className="ebb-table-wrap">
                <table className="module-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Enabled</th>
                      <th>Vacancy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(trekDetail.packages || [])
                      .filter((p) => !p.hidden && p.hide !== true)
                      .sort((a, b) => {
                        if (!!b.isMain !== !!a.isMain) return (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0);
                        return (Number(a.priority) || 0) - (Number(b.priority) || 0);
                      })
                      .map((p) => {
                        const id = String(p.id);
                        const row = availabilityDraft.packages?.[id] || { enabled: true, vacancy: '' };
                        return (
                          <tr key={id}>
                            <td>{p.name || '—'}</td>
                            <td style={{ maxWidth: 260 }}>{(p.description || '').trim() || '—'}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.enabled !== false}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  setAvailabilityDraft((d) => ({
                                    ...d,
                                    packages: { ...d.packages, [id]: { ...row, enabled: on } }
                                  }));
                                }}
                                disabled={!canManage}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min={0}
                                style={{ width: 88 }}
                                value={row.vacancy === '' || row.vacancy == null ? '' : row.vacancy}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setAvailabilityDraft((d) => ({
                                    ...d,
                                    packages: {
                                      ...d.packages,
                                      [id]: { ...row, vacancy: v === '' ? '' : Number(v) }
                                    }
                                  }));
                                }}
                                placeholder="Default"
                                disabled={!canManage}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    {!(trekDetail.packages || []).filter((p) => !p.hidden && p.hide !== true).length ? (
                      <tr>
                        <td colSpan={4}>No packages on this trek. Add packages under Treks first.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {(trekDetail.stayOptions || []).length > 0 ? (
                <>
                  <h3 className="ebb-section-title" style={{ fontSize: '0.95rem', marginTop: '1.25rem' }}>
                    Stay options
                  </h3>
                  <div className="ebb-table-wrap">
                    <table className="module-table" style={{ minWidth: 480 }}>
                      <thead>
                        <tr>
                          <th>Option</th>
                          <th>Enabled</th>
                          <th>Vacancy (optional)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(trekDetail.stayOptions || []).map((o) => {
                          const id = String(o.id || '').trim();
                          if (!id) return null;
                          const row = availabilityDraft.stayOptions?.[id] || { enabled: true, vacancy: '' };
                          return (
                            <tr key={id}>
                              <td>{o.name || id}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={row.enabled !== false}
                                  onChange={(e) => {
                                    const on = e.target.checked;
                                    setAvailabilityDraft((d) => ({
                                      ...d,
                                      stayOptions: { ...d.stayOptions, [id]: { ...row, enabled: on } }
                                    }));
                                  }}
                                  disabled={!canManage}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min={0}
                                  style={{ width: 88 }}
                                  value={row.vacancy === '' || row.vacancy == null ? '' : row.vacancy}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setAvailabilityDraft((d) => ({
                                      ...d,
                                      stayOptions: {
                                        ...d.stayOptions,
                                        [id]: { ...row, vacancy: v === '' ? '' : Number(v) }
                                      }
                                    }));
                                  }}
                                  placeholder="—"
                                  disabled={!canManage}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          ) : form.customizeAvailability && form.trekId && !trekDetail?.id ? (
            <p className="ebb-hint" style={{ marginTop: '0.75rem' }}>
              Loading trek packages…
            </p>
          ) : null}
        </section>

        <div className="ebb-actions">
          <button className="module-btn" type="submit" disabled={!canManage || createMutation.isLoading || updateMutation.isLoading}>
            {isEdit ? 'Save changes' : 'Create batch'}
          </button>
          <button
            type="button"
            className="module-btn secondary"
            onClick={() => navigate('/event-batches')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventBatchForm;
